import { useCallback, useEffect, useRef, useState } from "react";
import {
  getGeneratorUrl,
  convertToVideoFile,
  downloadDataURL,
  isFileTypeValid,
  getProjectDetails,
  unsupportedLibraries,
  generatorIframeID,
} from "./token-renderer-utilities";

export interface RenderSettings {
  contractAddress: string;
  tokenId: string;
  fileType: string;
  height: number;
  width: number;
  duration?: number;
  fps?: number;
  renderDelay?: number;
}

interface RecorderResponse {
  generatorIframeRef: React.RefObject<HTMLIFrameElement>;
  startRendering: (settings: RenderSettings) => void;
  error: Error | null;
  message: string | null;
  isLoading: boolean | null;
}

export function useTokenRenderer(): RecorderResponse {
  const generatorIframeRef = useRef<HTMLIFrameElement>(null);
  const [error, setError] = useState<Error | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean | null>(false);
  const callCountRef = useRef(0);

  const stopRendering = () => {
    setIsLoading(false);
    callCountRef.current -= 1;
  };

  const invalidated = (errorString: string): void => {
    setError(new Error(errorString));
    setIsLoading(false);
    stopRendering();
    return;
  };

  const receiveIframeMessage = async (event: MessageEvent): Promise<void> => {
    const res = event.data;
    if (res.target === generatorIframeID && res.status === "finished") {
      if (res.data.blob) {
        const {
          contractAddress,
          tokenId,
          fileType,
          fps,
          height,
          width,
          renderDelay,
          duration,
        } = res.data;

        const renderSettings = {
          contractAddress,
          tokenId,
          fileType,
          fps,
          height,
          width,
          renderDelay,
          duration,
        };
        const webMBlob = res.data.blob;

        if (res.data.fileType === "mp4" || res.data.fileType === "gif") {
          const arrayBuffer = await webMBlob.arrayBuffer();

          const res = await convertToVideoFile(
            arrayBuffer,
            renderSettings,
            setError,
            setMessage,
            setIsLoading
          );
          stopRendering();

          if (res.error) {
            setError(new Error(res.error));
            setIsLoading(false);
          }
        }
      } else {
        if (res.data.tokenId) {
          downloadDataURL(
            res.data.imageData,
            `${res.data.tokenId}_${Date.now()}`,
            res.data.imageType,
            res.data.height,
            res.data.width
          );
          stopRendering();
        }
      }
    }
  };

  const settingsRef = useRef<RenderSettings | null>(null);
  const sendRenderSettingsOnIframeLoad = useCallback(() => {
    if (generatorIframeRef.current && settingsRef.current) {
      generatorIframeRef.current.contentWindow?.postMessage(
        settingsRef.current,
        "*"
      );
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data.status === "ready" &&
        event.data.target === generatorIframeID
      ) {
        sendRenderSettingsOnIframeLoad();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [sendRenderSettingsOnIframeLoad]);

  const startRendering = useCallback(async (settings: RenderSettings) => {
    if (callCountRef.current >= 5) {
      setError(
        new Error(
          "Rendering limit reached. Please wait for a download and then try again."
        )
      );
      return;
    }
    setIsLoading(true);
    callCountRef.current += 1;

    settingsRef.current = settings;
    const { contractAddress, fileType, tokenId, height, width, duration } =
      settings;

    setError(null);
    if (!generatorIframeRef.current) return;

    const projectData = await getProjectDetails(tokenId, contractAddress);

    if (projectData?.external_asset_dependency_count > 0) {
      return invalidated(
        "Projects with external dependencies are not currently supported."
      );
    }

    if (
      unsupportedLibraries.some((substring) =>
        projectData?.script?.includes(substring)
      ) ||
      unsupportedLibraries.some((substring) =>
        projectData?.script_type_and_version?.includes(substring)
      )
    ) {
      invalidated("This project uses an unsupported script");
    }

    if (!isFileTypeValid(fileType)) {
      invalidated(`Filetype ${fileType} is invalid. Please resubmit.`);
    }

    if (!height || !width || height <= 0 || width <= 0) {
      invalidated(
        `Canvas dimensions are invalid. Height and width must be positive numbers.`
      );
    }

    // Duration of 2 minutes was the maximum length video that we've currently tested -- anything greater will run as long as
    // it stays lower than the AWS Lambda request timeout (ie. 15 minutes)
    if (duration && duration > 120000) {
      invalidated(
        `Cannot choose a duration greater than 2 minutes at this time.`
      );
    }

    const generatorURL = getGeneratorUrl({ contractAddress, tokenId });

    generatorIframeRef.current.height = height.toString();
    generatorIframeRef.current.width = width.toString();

    // Set the iframe src to trigger loading the generator
    generatorIframeRef.current.src = generatorURL;

    generatorIframeRef.current.onload = () => {
      try {
        sendRenderSettingsOnIframeLoad();
      } catch (error) {
        console.error("Error recording canvas in iframe. ", error);
        setIsLoading(false);
        setError(error instanceof Error ? error : new Error(String(error)));
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("message", receiveIframeMessage);

      return () => window.removeEventListener("message", receiveIframeMessage);
    }
  }, []);

  return { generatorIframeRef, startRendering, error, message, isLoading };
}
